/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdDescription } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { Editor } from '@tinymce/tinymce-react';
import PropTypes from 'prop-types';

import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';

import CardSideBordered from '~/components/CardSideBordered';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import history from '~/services/history';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';

const statusOptions = [
    { value: 'draft', label: 'page.articles.status.draft' },
    { value: 'published', label: 'page.articles.status.published' },
    { value: 'archived', label: 'page.articles.status.archived' },
];

export default function ArticleForm(props) {
    const dispatch = useDispatch();
    const { match } = props;

    const article_id = match.params.id;
    const [operation, setOperation] = useState('create');

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');

    const [body, setBody] = useState({
        title: '',
        content: '',
        status: 'draft',
        category_id: null,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            } else if (status === 403) {
                setForbidden(true);
            }
            toast.error(message);
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadCategories() {
        await api
            .get(`admin/articles/categories?parent_id=root`)
            .then(response => {
                const list = response.data.map(c => ({
                    value: c.id,
                    label: c.name,
                    subcategories: c.subcategories || [],
                }));
                setCategories(list);
            })
            .catch(error => requestError(error));
    }

    async function loadSubcategories(categoryId) {
        if (!categoryId) {
            setSubcategories([]);
            return;
        }

        await api
            .get(`admin/articles/categories/${categoryId}`)
            .then(response => {
                const cat = response.data;
                if (cat.subcategories && cat.subcategories.length > 0) {
                    const list = cat.subcategories.map(s => ({
                        value: s.id,
                        label: s.name,
                    }));
                    setSubcategories(list);
                } else {
                    setSubcategories([]);
                }
            })
            .catch(error => requestError(error));
    }

    async function loadArticle(_id) {
        if (_id === 'new') {
            setOperation('create');
            setIsLoading(false);
        } else {
            setOperation('update');
            await api
                .get(`admin/articles/${_id}`)
                .then(async response => {
                    const a = response.data;
                    setBody({
                        title: a.title || '',
                        content: a.content || '',
                        status: a.status || 'draft',
                        category_id: a.category ? a.category.id : null,
                    });

                    if (a.category) {
                        await api
                            .get(`admin/articles/categories/${a.category.id}`)
                            .then(async catResponse => {
                                const cat = catResponse.data;
                                if (cat.parent) {
                                    setSelectedCategory(cat.parent.id);
                                    await loadSubcategories(cat.parent.id);
                                    setSelectedSubcategory(a.category.id);
                                } else {
                                    setSelectedCategory(a.category.id);
                                    await loadSubcategories(a.category.id);
                                }
                            })
                            .catch(() => {
                                setSelectedCategory(a.category.id);
                            });
                    }
                })
                .catch(error => requestError(error));
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadArticle(article_id);
    }, [article_id]);

    function handleCategoryChange(categoryId) {
        setSelectedCategory(categoryId);
        setSelectedSubcategory('');
        setSubcategories([]);

        if (categoryId) {
            loadSubcategories(categoryId);
            setBody({
                ...body,
                category_id: categoryId,
            });
        } else {
            setBody({
                ...body,
                category_id: null,
            });
        }
    }

    function handleSubcategoryChange(subcategoryId) {
        setSelectedSubcategory(subcategoryId);

        if (subcategoryId) {
            setBody({
                ...body,
                category_id: subcategoryId,
            });
        } else {
            setBody({
                ...body,
                category_id: selectedCategory || null,
            });
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setIsLoading(true);

        if (operation === 'create') {
            await api
                .post(`admin/articles`, body)
                .then(() => {
                    toast.success(
                        <LocaleMessage msg="page.articles.form.create_success" />
                    );
                    history.push(`/articles`);
                })
                .catch(error => requestError(error));
        } else {
            await api
                .put(`admin/articles/${article_id}`, body)
                .then(() => {
                    toast.success(
                        <LocaleMessage msg="page.articles.form.update_success" />
                    );
                    loadArticle(article_id);
                })
                .catch(error => requestError(error));
        }

        setIsLoading(false);
    }

    return (
        <PageContent
            title={
                operation === 'create' ? (
                    <LocaleMessage msg="page.articles.form.create.title" />
                ) : (
                    <LocaleMessage msg="page.articles.form.edit.title" />
                )
            }
            currentPage={
                operation === 'create' ? (
                    <LocaleMessage msg="page.articles.form.create.title" />
                ) : (
                    <LocaleMessage msg="page.articles.form.edit.title" />
                )
            }
            breadcrumbs={[
                {
                    url: '/articles',
                    title: <LocaleMessage msg="breadcrumbs.articles" />,
                },
            ]}
            loading={isLoading}
            forbidden={forbidden}
        >
            <>
                <div className="row full-body">
                    <div className="col-md-8 col-12 mb-5">
                        <form noValidate autoComplete="off">
                            <CardSideBordered
                                title={
                                    <LocaleMessage msg="page.articles.form.label.settings" />
                                }
                                Icon={MdDescription}
                            >
                                <>
                                    <div className="row">
                                        <div className="col-md-8 col-12 mb-5">
                                            <TextField
                                                id="article-title"
                                                label={
                                                    <LocaleMessage msg="page.articles.form.label.title" />
                                                }
                                                fullWidth
                                                value={body.title || ''}
                                                onChange={event => {
                                                    setBody({
                                                        ...body,
                                                        title:
                                                            event.target.value,
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-4 col-12 mb-5">
                                            <FormControl fullWidth>
                                                <InputLabel>
                                                    <LocaleMessage msg="page.articles.form.label.status" />
                                                </InputLabel>
                                                <Select
                                                    id="article-status"
                                                    value={
                                                        body.status || 'draft'
                                                    }
                                                    onChange={event => {
                                                        setBody({
                                                            ...body,
                                                            status:
                                                                event.target
                                                                    .value,
                                                        });
                                                    }}
                                                >
                                                    {statusOptions.map(s => (
                                                        <MenuItem
                                                            value={s.value}
                                                            key={s.value}
                                                        >
                                                            <LocaleMessage
                                                                msg={s.label}
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div className="col-md-6 col-12 mb-5">
                                            <FormControl fullWidth>
                                                <InputLabel>
                                                    <LocaleMessage msg="page.articles.form.label.category" />
                                                </InputLabel>
                                                <Select
                                                    id="article-category"
                                                    value={
                                                        selectedCategory || ''
                                                    }
                                                    onChange={event => {
                                                        handleCategoryChange(
                                                            event.target.value
                                                        );
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <LocaleMessage msg="page.articles.form.label.no_category" />
                                                    </MenuItem>
                                                    {categories.map(c => (
                                                        <MenuItem
                                                            value={c.value}
                                                            key={c.value}
                                                        >
                                                            {c.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        {subcategories.length > 0 ? (
                                            <div className="col-md-6 col-12 mb-5">
                                                <FormControl fullWidth>
                                                    <InputLabel>
                                                        <LocaleMessage msg="page.articles.form.label.subcategory" />
                                                    </InputLabel>
                                                    <Select
                                                        id="article-subcategory"
                                                        value={
                                                            selectedSubcategory ||
                                                            ''
                                                        }
                                                        onChange={event => {
                                                            handleSubcategoryChange(
                                                                event.target
                                                                    .value
                                                            );
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <LocaleMessage msg="page.articles.form.label.no_subcategory" />
                                                        </MenuItem>
                                                        {subcategories.map(
                                                            s => (
                                                                <MenuItem
                                                                    value={
                                                                        s.value
                                                                    }
                                                                    key={
                                                                        s.value
                                                                    }
                                                                >
                                                                    {s.label}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        ) : null}
                                        <div className="col-12 mb-5">
                                            <Editor
                                                apiKey="3gjp9jm1wg4lviknytk7kv9xdtnb9zgc575efua099jr71ov"
                                                value={body.content || ''}
                                                onEditorChange={content => {
                                                    setBody({
                                                        ...body,
                                                        content,
                                                    });
                                                }}
                                                init={{
                                                    height: 400,
                                                    menubar: true,
                                                    plugins: [
                                                        'anchor',
                                                        'autolink',
                                                        'charmap',
                                                        'codesample',
                                                        'emoticons',
                                                        'link',
                                                        'lists',
                                                        'media',
                                                        'searchreplace',
                                                        'table',
                                                        'visualblocks',
                                                        'wordcount',
                                                    ],
                                                    toolbar:
                                                        'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </>
                            </CardSideBordered>
                            <Button
                                className="p-3"
                                variant="contained"
                                color="primary"
                                onClick={event => handleSubmit(event)}
                                fullWidth
                                size="large"
                            >
                                <LocaleMessage msg="button.save" />
                            </Button>
                        </form>
                    </div>
                </div>
            </>
        </PageContent>
    );
}

ArticleForm.propTypes = {
    match: PropTypes.object.isRequired,
};
